// src/components/HeroSection.js
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

/* ----------------------------------------------------
   HERO SECTION — MULTI SLIDE
---------------------------------------------------- */
const HeroSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const totalSlides = 3;

  // auto-rotate slides
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % totalSlides);
    }, 8000); // 8 seconds per slide
    return () => clearInterval(interval);
  }, []);

  return (
    <HeroContainer id="home">
      <ParticleLayer />

      {/* LEFT: TEXT */}
      <HeroContent
        key={activeSlide}
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7 }}
      >
        {/* small label above heading */}
        <Kicker>
          {activeSlide === 0 && "AI Trading App"}
          {activeSlide === 1 && "DeFi Safety Scanner"}
          {activeSlide === 2 && "$TGEN Utility Token"}
        </Kicker>

        {/* HEADING */}
        <Headline>
          {activeSlide === 0 && (
            <>
              Your Personal <AIText>AI Trading Analyst</AIText>
            </>
          )}
          {activeSlide === 1 && (
            <>
              Scan Any Token with <AIText>RugShield</AIText>
            </>
          )}
          {activeSlide === 2 && (
            <>
              Power Up with <AIText>$TGEN</AIText>
            </>
          )}
          <Sparkline />
        </Headline>

        {/* SUBHEADLINE */}
        <Subheadline>
          {activeSlide === 0 &&
            "Trend Genie scans live markets, detects patterns, and gives you real-time AI powered trading insights, right in your pocket."}
          {activeSlide === 1 &&
            "RugShield scans token contracts, spots red flags, and gives you instant DeFi risk checks right before you ape in."}
          {activeSlide === 2 &&
            "$TGEN is the utility token that connects the Trend Genie ecosystem, unlocking higher limits, deeper AI insights and future governance."}
        </Subheadline>

        {/* CTA BUTTON */}
        <CTAButton
          href={
            activeSlide === 0
              ? "https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
              : activeSlide === 1
              ? "https://app.trendgenie.io"
              : "/whitepaper"
          }
          target={activeSlide === 2 ? "_self" : "_blank"}
        >
          {activeSlide === 0 && "Download Trend Genie App"}
          {activeSlide === 1 && "Scan a Token with RugShield"}
          {activeSlide === 2 && "Read $TGEN Whitepaper"}
        </CTAButton>

        {/* TRUST ROW */}
        <TrustRow>
          {activeSlide === 0 && (
            <>
              <TrustItem>✔ Trusted by 10k+ traders</TrustItem>
              <TrustItem>✔ AI insights updating every second</TrustItem>
              <TrustItem>✔ Beginner friendly & fast</TrustItem>
            </>
          )}

          {activeSlide === 1 && (
            <>
              <TrustItem>✔ Honeypot & rug pull checks</TrustItem>
              <TrustItem>✔ Read only · no private keys</TrustItem>
              <TrustItem>✔ Beginner & Pro explanation modes</TrustItem>
            </>
          )}

          {activeSlide === 2 && (
            <>
              <TrustItem>✔ Utility token · not equity</TrustItem>
              <TrustItem>✔ Fixed supply design</TrustItem>
              <TrustItem>✔ Built for power users & holders</TrustItem>
            </>
          )}
        </TrustRow>
      </HeroContent>

      {/* RIGHT: PHONE VISUAL */}
      <RightPhoneSection
        key={`phone-${activeSlide}`}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <NeuralWeb />
        <HoloRings />
        <PhoneGlow />

        <Crypto style={{ top: "-40px", left: "30px" }}>₿</Crypto>
        <Crypto style={{ top: "60px", right: "-30px" }}>Ξ</Crypto>
        <Crypto style={{ bottom: "-35px", left: "25px" }}>◎</Crypto>
        <Crypto style={{ bottom: "50%", right: "-20px" }}>₮</Crypto>

        <AIBadge>
          {activeSlide === 0 && "⚡ AI Market View"}
          {activeSlide === 1 && "🛡 DeFi Risk Scanner"}
          {activeSlide === 2 && "💠 TGEN Utility Token"}
        </AIBadge>

        <ScanBeam />

        <PhoneFrame className="phone-3d">
          {/* SLIDE 1 PHONE CONTENT — LIVE CHART */}
          {activeSlide === 0 && (
            <ChartWrapper>
              <ChartIframe
                src="https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=BINANCE:BTCUSD&interval=5&hidesidetoolbar=1&symboledit=1&saveimage=0&toolbarbg=rgba(0,0,0,0)&studies=&theme=dark&style=1&timezone=Etc/UTC&withdateranges=0&hideideas=1&hidelegend=1&hidevolume=1"
                title="BTC Chart"
              />
            </ChartWrapper>
          )}

          {/* SLIDE 2 PHONE CONTENT — RUGSHIELD MOCKUP */}
          {activeSlide === 1 && (
            <ChartWrapper>
              <ChartIframe
                src="https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=BINANCE:BTCUSD&interval=5&hidesidetoolbar=1&symboledit=1&saveimage=0&toolbarbg=rgba(0,0,0,0)&studies=&theme=dark&style=1&timezone=Etc/UTC&withdateranges=0&hideideas=1&hidelegend=1&hidevolume=1"
                title="TGEN Chart"
              />
            </ChartWrapper>
          )}

          {/* SLIDE 3 PHONE CONTENT — TGEN TOKEN MOCKUP */}
          {activeSlide === 2 && (
            <ChartWrapper>
              <ChartIframe
                src="https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=BINANCE:BTCUSD&interval=5&hidesidetoolbar=1&symboledit=1&saveimage=0&toolbarbg=rgba(0,0,0,0)&studies=&theme=dark&style=1&timezone=Etc/UTC&withdateranges=0&hideideas=1&hidelegend=1&hidevolume=1"
                title="TGEN Chart"
              />
            </ChartWrapper>
          )}

          <HoloLines />
        </PhoneFrame>

        <HoloBase />
      </RightPhoneSection>

      {/* HERO SLIDE DOTS */}
      <HeroDots>
        {[0, 1, 2].map((index) => (
          <HeroDot
            key={index}
            type="button"
            active={activeSlide === index}
            onClick={() => setActiveSlide(index)}
          />
        ))}
      </HeroDots>
    </HeroContainer>
  );
};

export default HeroSection;

/* ----------------------------------------------------
   STYLES
---------------------------------------------------- */

const HeroContainer = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 95vh;
  padding: 0 10%;
  overflow: hidden;
  background: radial-gradient(circle at 20% 20%, #1f2345, #0a0c15, #000);
  position: relative;

  @media (max-width: 992px) {
    flex-direction: column;
    padding: 4rem 1.5rem;
  }

  @media (max-width: 600px) {
    padding: 3rem 1rem;
  }
`;

/* LEFT CONTENT */

const HeroContent = styled(motion.div)`
  max-width: 48%;
  z-index: 10;

  @media (max-width: 992px) {
    max-width: 100%;
    margin-bottom: 3rem;
    text-align: center;
  }
`;

const Kicker = styled.p`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #8f9bff;
  margin-bottom: 0.6rem;

  @media (max-width: 992px) {
    text-align: center;
  }
`;

const Headline = styled.h1`
  font-size: 4rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
  @media (max-width: 480px) {
    font-size: 2.3rem;
  }
`;

const AIText = styled.span`
  background: linear-gradient(90deg, #ae66ff, #d7a8ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Sparkline = styled.div`
  width: 160px;
  height: 3px;
  background: linear-gradient(90deg, transparent, #c38aff, #ae66ff, transparent);
  margin-top: 8px;
  border-radius: 3px;

  @media (max-width: 992px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const Subheadline = styled.p`
  font-size: 1.05rem;
  color: #cfd2e6;
  margin-bottom: 2rem;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled.a`
  padding: 1rem 2.7rem;
  border-radius: 14px;
  background: linear-gradient(90deg, #7c4dff, #b27aff);
  color: white;
  font-weight: 600;
  text-decoration: none;

  @media (max-width: 992px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const TrustRow = styled.div`
  margin-top: 2rem;
  display: flex;
  gap: 2rem;
  color: #bfc3dc;
  flex-wrap: wrap;

  @media (max-width: 992px) {
    justify-content: center;
    gap: 1rem;
    font-size: 0.9rem;
  }
`;

const TrustItem = styled.div``;

/* PARTICLES */

const ParticleLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;

  background: radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px),
              radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-position: 0 0, 30px 30px;
  background-size: 60px 60px;
  opacity: 0.12;

  @media (max-width: 992px) {
    opacity: 0.06;
  }
`;

/* NEURAL WEB */

const NeuralWeb = styled.div`
  position: absolute;
  width: 140%;
  height: 140%;
  top: -20%;
  left: -20%;
  z-index: 1;
  opacity: 0.15;

  background: conic-gradient(
    from 0deg,
    rgba(160,80,255,0.15),
    rgba(120,60,255,0.05),
    rgba(180,110,255,0.12),
    rgba(120,60,255,0.05),
    rgba(160,80,255,0.15)
  );

  animation: neuralSpin 18s linear infinite;

  @keyframes neuralSpin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @media (max-width: 992px) {
    opacity: 0.08;
  }
`;

/* HOLO LINES */

const HoloLines = styled.div`
  position: absolute;
  width: 120%;
  height: 120%;
  top: -10%;
  left: -10%;
  z-index: 1;
  pointer-events: none;

  background: repeating-linear-gradient(
    90deg,
    rgba(180,120,255,0.12),
    rgba(180,120,255,0.12) 2px,
    transparent 2px,
    transparent 18px
  );

  filter: blur(1px);
  opacity: 0.08;

  animation: holoSlide 12s linear infinite;

  @keyframes holoSlide {
    from { transform: translateX(0); }
    to { transform: translateX(-80px); }
  }

  @media (max-width: 992px) {
    opacity: 0.04;
  }
`;

/* RIGHT PHONE AREA */

const RightPhoneSection = styled(motion.div)`
  position: relative;
  width: 26vw;
  max-width: 360px;
  height: 40vw;
  max-height: 720px;
  min-width: 260px;
  z-index: 5;

  .phone-3d {
    transition: transform 0.35s ease;
  }

  &:hover .phone-3d {
    transform: perspective(900px) rotateY(-12deg) scale(1.02);
  }

  @media (max-width: 992px) {
    width: 80vw;
    height: 140vw;
    max-width: 330px;
    max-height: 600px;
    margin-top: 2rem;
  }

  @media (max-width: 480px) {
    width: 85vw;
    height: 150vw;
    max-width: 300px;
    max-height: 550px;
  }
`;

const PhoneFrame = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 28px;
  overflow: hidden;
  background: #000;
  border: 2px solid rgba(150, 80, 255, 0.4);
  box-shadow: 0 0 40px rgba(150, 80, 255, 0.55),
              inset 0 0 20px rgba(150, 80, 255, 0.3);
  position: relative;
  z-index: 3;
`;

/* PHONE CONTENT BASE WRAPPERS */

const ChartWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const ChartIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

/* RUGSHIELD MOCKUP */

const ScannerMock = styled.div`
  position: absolute;
  inset: 0;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: radial-gradient(circle at top, #131b3a, #050712);
  color: #e6e9ff;
  font-size: 0.78rem;
`;

const ScannerHeader = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #f7f1ff;
`;

const ScannerInput = styled.div`
  margin-top: 2px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(15, 18, 40, 0.9);
  border: 1px solid rgba(120, 140, 255, 0.45);
  display: flex;
  flex-direction: column;
  gap: 3px;

  span {
    font-size: 0.7rem;
    color: #a6a9d6;
  }

  code {
    font-size: 0.78rem;
    color: #f9f9ff;
  }
`;

const ScannerRiskRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const RiskPill = styled.div`
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  background: ${(p) =>
    p.level === "low"
      ? "rgba(46, 204, 113, 0.12)"
      : p.level === "med"
      ? "rgba(241, 196, 15, 0.12)"
      : "rgba(231, 76, 60, 0.12)"};
  color: ${(p) =>
    p.level === "low"
      ? "#2ecc71"
      : p.level === "med"
      ? "#f1c40f"
      : "#e74c3c"};
  border: 1px solid currentColor;
`;

const ScannerResult = styled.div`
  margin-top: 4px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(12, 16, 35, 0.95);
  display: flex;
  align-items: center;
  gap: 10px;

  .label {
    font-size: 0.7rem;
    color: #a6a9d6;
    margin: 0;
  }

  .value {
    font-size: 0.8rem;
    color: #f7f1ff;
    margin: 2px 0 0;
  }
`;

const ScannerLight = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: ${(p) =>
    p.level === "low"
      ? "radial-gradient(circle, #2ecc71, transparent)"
      : p.level === "med"
      ? "radial-gradient(circle, #f1c40f, transparent)"
      : "radial-gradient(circle, #e74c3c, transparent)"};
`;

const ScannerFooter = styled.div`
  margin-top: auto;
  font-size: 0.7rem;
  color: #8f95d0;
`;

/* TOKEN MOCKUP */

const TokenMock = styled.div`
  position: absolute;
  inset: 0;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: radial-gradient(circle at top, #1b1230, #050712);
  color: #f6f2ff;
  font-size: 0.78rem;
`;

const TokenHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .symbol {
    font-weight: 700;
    font-size: 0.95rem;
  }

  .chip {
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 0.7rem;
    background: rgba(88, 240, 255, 0.12);
    color: #58f0ff;
  }
`;

const TokenStatRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 4px;

  label {
    display: block;
    font-size: 0.7rem;
    color: #c7c3ff;
  }

  strong {
    font-size: 0.8rem;
  }
`;

const TokenBenefits = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 0.8rem;

  li {
    margin-bottom: 3px;
  }
`;

const TokenFooter = styled.div`
  margin-top: auto;
  font-size: 0.7rem;
  color: #c7c3ff;
`;

/* BADGE */

const AIBadge = styled.div`
  position: absolute;
  top: -40px;
  right: 0;
  padding: 8px 18px;
  background: rgba(255,255,255,0.08);
  border-radius: 10px;
  font-size: 14px;
  border: 1px solid rgba(200,150,255,0.4);
  backdrop-filter: blur(12px);
  color: white;
  z-index: 30;

  @media (max-width: 992px) {
    top: -30px;
    font-size: 12px;
    padding: 6px 14px;
  }
`;

/* CRYPTO FLOATERS */

const Crypto = styled.div`
  position: absolute;
  font-size: 2vw;
  color: #c697ff;
  opacity: 0.25;
  z-index: 20;

  @media (max-width: 992px) {
    font-size: 20px;
  }
`;

/* HOLOGRAM & GLOW */

const ScanBeam = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(150,80,255,0.15),
    rgba(200,150,255,0.06),
    rgba(150,80,255,0.15)
  );
  animation: scan 5s linear infinite;
  pointer-events: none;
  z-index: 15;

  @keyframes scan {
    from { transform: translateY(-100%); }
    to { transform: translateY(100%); }
  }
`;

const PhoneGlow = styled.div`
  position: absolute;
  width: 480px;
  height: 480px;
  background: radial-gradient(circle, rgba(160,80,255,0.4), transparent);
  filter: blur(80px);
  top: 10vw;
  left: -10vw;
  z-index: 1;

  @media (max-width: 992px) {
    width: 260px;
    height: 260px;
    top: 120px;
    left: -40px;
    filter: blur(50px);
  }
`;

const HoloRings = styled.div`
  position: absolute;
  width: 40vw;
  height: 40vw;
  max-width: 420px;
  max-height: 420px;
  border-radius: 50%;
  border: 2px dashed rgba(150,80,255,0.15);
  animation: spin 14s linear infinite;
  top: 6vw;
  left: -6vw;
  z-index: 2;

  @media (max-width: 992px) {
    width: 260px;
    height: 260px;
    top: 60px;
    left: -30px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

/* BASE GLOW */

const HoloBase = styled.div`
  position: absolute;
  bottom: -2vw;
  width: 100%;
  height: 4vw;
  background: radial-gradient(circle, rgba(160,80,255,0.5), transparent);
  filter: blur(2vw);
  z-index: 1;

  @media (max-width: 992px) {
    height: 30px;
    filter: blur(20px);
    bottom: -10px;
  }
`;

/* HERO SLIDE DOTS */

const HeroDots = styled.div`
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 20;

  @media (max-width: 992px) {
    bottom: 24px;
  }
`;

const HeroDot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${(p) =>
    p.active ? "#b27aff" : "rgba(255,255,255,0.35)"};
  opacity: ${(p) => (p.active ? 1 : 0.6)};
`;
